import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { paymentService } from '../services/api';
import { Payment } from '../types';

const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const PaymentsLedger: React.FC = () => {
  const user = useMemo(() => getCurrentUser(), []);
  const hostelId = Number(user?.hostelId || 0);

  const paymentsQuery = useQuery({
    queryKey: ['payments', hostelId],
    enabled: !!hostelId,
    queryFn: async (): Promise<Payment[]> => {
      try {
        return await paymentService.getPaymentsByHostel(hostelId);
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load payments');
        throw e;
      }
    },
  });

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Payments</h3>
          <div className="text-muted">Ledger of recorded payments (latest 500)</div>
        </div>
        <button className="btn btn-outline-primary" onClick={() => paymentsQuery.refetch()} disabled={paymentsQuery.isFetching}>
          {paymentsQuery.isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive responsive-table-wrap">
            <table className="table table-hover mb-0 responsive-table">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Paid At</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Invoice</th>
                  <th>Due</th>
                  <th>Ref</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(paymentsQuery.data || []).length === 0 ? (
                  <tr className="responsive-table__empty">
                    <td colSpan={9} className="text-center py-4 text-muted" data-label="">
                      {paymentsQuery.isLoading ? 'Loading...' : 'No payments found.'}
                    </td>
                  </tr>
                ) : (
                  (paymentsQuery.data || []).map((p) => (
                    <tr key={p.id}>
                      <td data-label="ID">{p.id}</td>
                      <td data-label="Paid at">{p.paidAt ? new Date(p.paidAt).toLocaleString() : '-'}</td>
                      <td data-label="User">{p.userId}</td>
                      <td className="responsive-table__amount" data-label="Amount">₹{Number(p.amount).toFixed(2)}</td>
                      <td data-label="Method">{p.method}</td>
                      <td data-label="Invoice">{p.invoiceId ?? '-'}</td>
                      <td data-label="Due">{p.dueId ?? '-'}</td>
                      <td className="responsive-table__full" data-label="Ref">
                        {p.reference || '-'}
                      </td>
                      <td data-label="Status">
                        <span className={`badge ${p.status === 'SUCCESS' ? 'bg-success' : p.status === 'FAILED' ? 'bg-danger' : 'bg-secondary'}`}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsLedger;

